import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserRole } from '@lysi-web/shared-types';
import apiClient from '../lib/axios';
import { queryClient } from '../lib/queryClient';
import { ChangePasswordModal } from '../components/ChangePasswordModal';
import { roleHome } from '../lib/roleHome';

export interface User {
  id: string;
  email: string;
  role: UserRole;
  doctorId?: string;
  drugstoreId?: string;
  mustChangePassword: boolean;
  // Doctor-specific fields
  firstName?: string;
  lastName?: string;
  specialty?: string;
  licenseNumber?: string;
  // Doctor feature flags
  canPrescribe?: boolean;
  canViewPatients?: boolean;
  // Consultant-specific fields
  consultantId?: string;
  fullName?: string;
  pharmaceuticalLabId?: string;
  pharmaceuticalLabName?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  showPasswordChangeModal: boolean;
  login: (email: string, password: string) => Promise<User>;
  loginWithGoogle: (idToken: string) => Promise<User>;
  logout: () => Promise<void>;
  refetchUser: () => Promise<void>;
  handlePasswordChangeSuccess: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const navigate = useNavigate();

  const isAuthenticated = !!user;

  // Fetch current user profile
  const fetchUser = async () => {
    const accessToken = localStorage.getItem('accessToken');

    if (!accessToken) {
      setIsLoading(false);
      return;
    }

    try {
      const { data } = await apiClient.get<User>('/auth/me');
      setUser(data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('accessToken');
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize auth state on mount
  useEffect(() => {
    fetchUser();
  }, []);

  const navigateToDashboard = (role: UserRole) => {
    navigate(roleHome(role));
  };

  const login = async (email: string, password: string): Promise<User> => {
    const { data } = await apiClient.post<{ accessToken: string; user: User }>('/auth/login', {
      email,
      password,
    });

    // Store access token
    localStorage.setItem('accessToken', data.accessToken);

    // Set user in context
    setUser(data.user);

    // Check if user must change password
    if (data.user.mustChangePassword) {
      setShowPasswordChangeModal(true);
    }

    return data.user;
  };

  const loginWithGoogle = async (idToken: string): Promise<User> => {
    const { data } = await apiClient.post<{ accessToken: string; user: User }>('/auth/google', { idToken });
    localStorage.setItem('accessToken', data.accessToken);
    setUser(data.user);
    return data.user;
  };

  const handlePasswordChangeSuccess = async () => {
    // Close modal
    setShowPasswordChangeModal(false);

    // Refetch user to get updated mustChangePassword flag
    await refetchUser();

    // Navigate to dashboard
    if (user) {
      navigateToDashboard(user.role);
    }
  };

  const logout = async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local state regardless of API call success
      localStorage.removeItem('accessToken');
      setUser(null);
      queryClient.clear();
      window.dispatchEvent(new Event('cart:clear'));
      navigate('/login');
    }
  };

  const refetchUser = async () => {
    await fetchUser();
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    showPasswordChangeModal,
    login,
    loginWithGoogle,
    logout,
    refetchUser,
    handlePasswordChangeSuccess,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <ChangePasswordModal
        open={showPasswordChangeModal}
        onClose={() => {}}
        onSuccess={handlePasswordChangeSuccess}
        isRequired={true}
      />
    </AuthContext.Provider>
  );
};
